import { useQuery } from '@tanstack/react-query'
import { api, apiErrorFromResponse, authFetch } from '@/api/client'
import { queryKeys } from '@/api/queryKeys'
import { ApiError, type ProblemDetail } from '@/lib/errors'
import type { components } from '@/../types/api'

export type VideoSnapshot = components['schemas']['VideoSnapshotOut']

export interface UseVideoSnapshotsParams {
  videoId: string
  lang: string
}

/**
 * YouTube Analytics time series for a single video — ordered by
 * snapshot_age (t48h, t7d, t30d, ...). Empty until the collector worker
 * samples the video; fresh uploads will return [] until at least the
 * first sample lands.
 */
export function useVideoSnapshots({ videoId, lang }: UseVideoSnapshotsParams) {
  return useQuery({
    queryKey: queryKeys.analytics.videoSnapshots(videoId, lang),
    queryFn: async (): Promise<VideoSnapshot[]> => {
      const { data } = await api.GET('/api/v1/analytics/videos/{video_id}/snapshots', {
        params: { path: { video_id: videoId }, query: { lang } },
      })
      if (!data) return []
      return data.data
    },
    enabled: Boolean(videoId && lang),
    staleTime: 60_000, // backend writes infrequently (collector cron)
  })
}

export type VideoYoutube = components['schemas']['VideoYoutubeOut']
export type VideoComment = components['schemas']['VideoCommentOut']

/**
 * Current YouTube counters (views, likes, comments — the hourly stats sync)
 * and the comment list of one published video. Unlike the snapshots, these
 * exist from the first sync after publication. Each fetch reads the comments
 * live (1 quota unit), hence the longer staleTime.
 */
export function useVideoYoutube({ videoId, lang }: UseVideoSnapshotsParams) {
  return useQuery({
    queryKey: queryKeys.analytics.videoYoutube(videoId, lang),
    queryFn: async (): Promise<VideoYoutube | null> => {
      const { data } = await api.GET('/api/v1/analytics/videos/{video_id}/youtube', {
        params: { path: { video_id: videoId }, query: { lang } },
      })
      return data?.data ?? null
    },
    enabled: Boolean(videoId && lang),
    staleTime: 5 * 60_000,
  })
}

// ---------------------------------------------------------------------------
// Peak hours report — types come from the regenerated OpenAPI client.
// ---------------------------------------------------------------------------

export type PeakHourBucket = components['schemas']['PeakHourBucket']
export type PeakHourVideo = components['schemas']['PeakHourVideo']
export type PeakHoursReport = components['schemas']['PeakHoursOut']

interface Envelope<T> {
  data: T
}

export interface UsePeakHoursParams {
  channel: string
  /** Lookback window in days: 0 = the whole history, else [7, 365]. */
  days?: number
  /** IANA timezone for hour bucketing (default America/Sao_Paulo). */
  tz?: string
}

/**
 * Per-channel peak posting hours. Server pulls publishedAt + view counts
 * from the YouTube Data API and buckets by hour-of-day in ``tz``. Median
 * is the headline metric (robust to viral outliers); ``best_hour`` is None
 * until at least one hour collects 2+ samples.
 *
 * Costs ~10 quota units worst case (videos.list batches of 50). 5 min
 * staleTime so revisits within a session don't rehit the API.
 */
export function usePeakHours({ channel, days = 90, tz = 'America/Sao_Paulo' }: UsePeakHoursParams) {
  return useQuery({
    queryKey: queryKeys.analytics.peakHours({ channel, days, tz }),
    queryFn: async (): Promise<PeakHoursReport> => {
      const params = new URLSearchParams()
      params.set('channel', channel)
      params.set('days', String(days))
      params.set('tz', tz)
      const res = await authFetch(`/api/v1/analytics/peak-hours?${params.toString()}`, {
        credentials: 'same-origin',
      })
      if (!res.ok) {
        let body: unknown = null
        try {
          body = await res.clone().json()
        } catch {
          /* ignore */
        }
        const problem = isProblemDetail(body) ? body : null
        const message = problem?.detail ?? problem?.title ?? ''
        throw new ApiError(message, res.status, problem, body)
      }
      const json = (await res.json()) as Envelope<PeakHoursReport>
      return json.data
    },
    enabled: Boolean(channel),
    staleTime: 5 * 60_000,
  })
}

function isProblemDetail(body: unknown): body is ProblemDetail {
  if (!body || typeof body !== 'object') return false
  const obj = body as Record<string, unknown>
  return typeof obj['title'] === 'string' && typeof obj['status'] === 'number'
}

// ---------------------------------------------------------------------------
// Retention audit — deterministic per-video verdicts.
// ---------------------------------------------------------------------------

export type RetentionAuditReport = components['schemas']['RetentionAuditOut']
export type RetentionAuditItem = components['schemas']['RetentionAuditItemOut']
export type RetentionAuditSummary = components['schemas']['RetentionAuditSummaryOut']
export type RetentionVerdict = RetentionAuditItem['verdict']
export type RetentionAgeBucket = RetentionAuditItem['age_bucket']
export type RetentionRetBucket = RetentionAuditItem['retention_bucket']

export interface UseRetentionAuditParams {
  channel: string
  limit?: number
  offset?: number
}

/**
 * Per-video keep / edit_thumb_title / wait / kill verdicts for a channel.
 * Server reads ``youtube_uploads`` + ``youtube_video_stats`` +
 * ``video_performance_snapshots`` populated by the existing crons; the
 * verdict matrix is documented in ``services/analytics/retention_audit.py``.
 *
 * Hybrid response: ``summary`` is computed on the full set, ``items`` is
 * a paginated slice. 60s staleTime so revisits inside a session don't
 * thrash the DB after a stats_sync tick.
 */
export function useRetentionAudit({ channel, limit = 25, offset = 0 }: UseRetentionAuditParams) {
  return useQuery({
    queryKey: queryKeys.analytics.retentionAudit({ channel, limit, offset }),
    queryFn: async (): Promise<RetentionAuditReport> => {
      const params = new URLSearchParams()
      params.set('channel', channel)
      params.set('limit', String(limit))
      params.set('offset', String(offset))
      const res = await authFetch(`/api/v1/analytics/retention-audit?${params.toString()}`)
      if (!res.ok) {
        let body: unknown = null
        try {
          body = await res.clone().json()
        } catch {
          /* ignore */
        }
        const problem = isProblemDetail(body) ? body : null
        const message = problem?.detail ?? problem?.title ?? ''
        throw new ApiError(message, res.status, problem, body)
      }
      const json = (await res.json()) as Envelope<RetentionAuditReport>
      return json.data
    },
    enabled: Boolean(channel),
    staleTime: 60_000,
    placeholderData: (prev) => prev,
    // Uma capa na fila do Studio vira "aplicada" em ~1 min: só nesse
    // intervalo vale reconsultar sozinho (card #247).
    refetchInterval: (query) =>
      query.state.data?.items.some((i) => i.studio_thumbnail_status === 'pending') ? 30_000 : false,
  })
}

// ---------------------------------------------------------------------------
// Correlation report — types come from the regenerated OpenAPI client.
// ---------------------------------------------------------------------------

export type CorrelationReport = components['schemas']['CorrelationOut']
export type FeatureBucket = components['schemas']['FeatureBucketOut']
export type CorrelationMetric = CorrelationReport['metric']

export interface UseCorrelationParams {
  channel: string
  metric?: CorrelationMetric
  /** Lookback window in days. Server clamps to [7, 365]. */
  days?: number
}

/**
 * Per-feature distribution of a chosen metric across a channel's videos.
 * Reads from Postgres only — backed by the hourly stats_sync + daily
 * snapshots_sync crons. Skips rows missing the chosen metric (e.g.
 * ``like_rate`` requires ``view_count > 0``; ``retention`` requires a
 * ``t7d`` snapshot).
 */
export function useCorrelation({ channel, metric = 'views', days = 90 }: UseCorrelationParams) {
  return useQuery({
    queryKey: queryKeys.analytics.correlation({ channel, metric, days }),
    queryFn: async (): Promise<CorrelationReport> => {
      const params = new URLSearchParams()
      params.set('channel', channel)
      params.set('metric', metric)
      params.set('days', String(days))
      const res = await authFetch(`/api/v1/analytics/correlation?${params.toString()}`, {
        credentials: 'same-origin',
      })
      if (!res.ok) {
        let body: unknown = null
        try {
          body = await res.clone().json()
        } catch {
          /* ignore */
        }
        const problem = isProblemDetail(body) ? body : null
        const message = problem?.detail ?? problem?.title ?? ''
        throw new ApiError(message, res.status, problem, body)
      }
      const json = (await res.json()) as Envelope<CorrelationReport>
      return json.data
    },
    enabled: Boolean(channel),
    // Backend reads from DB only — cheap to recompute. 60s staleTime is
    // enough to dedupe within a session without showing stale buckets
    // after a stats_sync cron tick.
    staleTime: 60_000,
  })
}


// ---------------------------------------------------------------------------
// External uploads — Sub-phase 1 of the /analytics-includes-externals item.
// Powers the "Externo" rows in the analytics published-videos table.
// ---------------------------------------------------------------------------

export interface ExternalUploadEntry {
  external_id: string
  title: string
  thumbnail_url: string
  view_count: number
  like_count: number
  comment_count: number
  /** TikTok-only: shares + deep-link URL. YT rows leave both at defaults. */
  share_count: number
  share_url: string
  published_at: string | null
}

export interface ExternalUploadsResponse {
  items: ExternalUploadEntry[]
}

export function useExternalUploads(channel: string) {
  return useQuery({
    queryKey: [...queryKeys.analytics.all, 'external-uploads', channel] as const,
    queryFn: async (): Promise<ExternalUploadsResponse> => {
      const res = await authFetch(
        `/api/v1/analytics/external-uploads?channel=${encodeURIComponent(channel)}`,
        { credentials: 'same-origin' },
      )
      if (!res.ok) throw await apiErrorFromResponse(res, 'Falha ao carregar os uploads externos')
      // Partial<>: `Envelope<T>.data` é obrigatório no tipo, então o cast
      // afirmava que o campo sempre vem e o `??` virava código morto aos olhos
      // do eslint. Mas isto é resposta de rede — o tipo é uma promessa, não
      // uma garantia. Tipar como parcial mantém o fallback legítimo.
      const json = (await res.json()) as Partial<Envelope<ExternalUploadsResponse>>
      return json.data ?? { items: [] }
    },
    enabled: Boolean(channel),
    // Backend reads from DB only (youtube_external_stats), populated by
    // the hourly cron. 60s dedup mirrors the other analytics queries.
    staleTime: 60_000,
  })
}

// ---------------------------------------------------------------------------
// Channel comparison — cross-channel performance for a whole platform.
// Hand-written types (endpoint added in this branch, not yet in the
// regenerated types/api.ts). Swap for ``components['schemas'][...]`` once
// ``scripts/gen_ts_client.sh`` re-runs — same precedent as reports.ts.
// ---------------------------------------------------------------------------

export type ChannelStatus = 'growing' | 'stable' | 'stalling' | 'dormant'

export interface ChannelComparisonRow {
  channel_slug: string
  channel_name: string
  video_count_window: number
  video_count_lifetime: number
  views_sum: number
  likes_sum: number
  comments_sum: number
  views_median: number
  views_mean: number
  /** avp median in [0,1]; null when the platform/env has no retention data. */
  retention_median: number | null
  like_rate: number | null
  comment_rate: number | null
  last_upload_at: string | null
  days_since_last_upload: number | null
  /** Relative change in early reach vs the prior cohort; null when unknown. */
  momentum_pct: number | null
  dead_pct: number
  status: ChannelStatus
  status_reason: string
  window_cost_usd: number
  /** Views per USD of generation cost; null when no cost recorded. */
  views_per_usd: number | null
  /** The two cohort medians behind ``momentum_pct`` (null with it). */
  momentum_recent_median: number | null
  momentum_previous_median: number | null
  /** Length of each momentum cohort: the window, capped at 90 ("Tudo" = 90). */
  momentum_cohort_days: number
}

export interface ChannelComparison {
  platform: string
  days: number
  channels: ChannelComparisonRow[]
  /** Which optional panels have data: retention, momentum, cost, shares. */
  metric_availability: Record<string, boolean>
}

export interface UseChannelCompareParams {
  platform: string
  /** Lookback window in days. Server clamps to [7, 365]. */
  days?: number
}

/**
 * Cross-channel comparison for one platform. Platform-agnostic on the
 * backend — each platform normalizes its own tables into a shared shape, so
 * this same hook powers TikTok once its loader ships. ``metric_availability``
 * flags which optional panels (retention / momentum / cost) actually have
 * data so the UI can hide empty ones (retention + momentum are absent in dev
 * without collected snapshots).
 */
export function useChannelCompare({ platform, days = 90 }: UseChannelCompareParams) {
  return useQuery({
    queryKey: queryKeys.analytics.compare({ platform, days }),
    queryFn: async (): Promise<ChannelComparison> => {
      const params = new URLSearchParams()
      params.set('platform', platform)
      params.set('days', String(days))
      const res = await authFetch(`/api/v1/analytics/compare?${params.toString()}`, {
        credentials: 'same-origin',
      })
      if (!res.ok) {
        let body: unknown = null
        try {
          body = await res.clone().json()
        } catch {
          /* ignore */
        }
        const problem = isProblemDetail(body) ? body : null
        const message = problem?.detail ?? problem?.title ?? ''
        throw new ApiError(message, res.status, problem, body)
      }
      const json = (await res.json()) as Envelope<ChannelComparison>
      return json.data
    },
    enabled: Boolean(platform),
    // DB-only read; 60s dedup mirrors the other analytics queries.
    staleTime: 60_000,
  })
}
